import { useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ShieldAlert, LogOut } from 'lucide-react';

export default function NoAccessPage() {
  const { user, userData, advertiser, isAdvertiser, isSuperAdmin, loading, signOut } = useAuth();
  const navigate = useNavigate();

  // Defensive self-correction: if auth state says this user is actually a valid
  // advertiser (e.g. race on login before the mirror doc loads) redirect them.
  useEffect(() => {
    if (loading || !user) return;
    if (isSuperAdmin) {
      navigate('/admin/advertisers', { replace: true });
    } else if (isAdvertiser && advertiser?.status === 'active') {
      navigate('/', { replace: true });
    } else if (isAdvertiser && advertiser?.status === 'paused') {
      navigate('/wallet', { replace: true });
    } else if (isAdvertiser && advertiser?.status === 'pending') {
      navigate('/pending-approval', { replace: true });
    }
  }, [loading, user, isSuperAdmin, isAdvertiser, advertiser, navigate]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4 text-fg">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand font-serif-display text-lg text-brand-ink">
            g
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-serif-display text-xl leading-none text-fg">grobot</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">Ads</span>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-line bg-bg-elev">
          <div className="border-b border-line px-6 py-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-danger/10">
              <ShieldAlert className="h-7 w-7 text-danger" />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">Access blocked</div>
            <h1 className="mt-2 font-serif-display text-3xl leading-none text-fg">No advertiser access</h1>
            <p className="mt-3 text-sm text-fg-dim">
              Your account{' '}
              {userData?.email && (
                <>
                  (<span className="font-medium text-fg">{userData.email}</span>){' '}
                </>
              )}
              isn't linked to an advertiser profile.
            </p>
          </div>

          <div className="space-y-4 px-6 py-6">
            <p className="text-center text-sm text-fg-dim">
              To get started,{' '}
              <Link to="/signup" className="font-medium text-brand underline underline-offset-2">
                apply for advertiser access
              </Link>{' '}
              or email{' '}
              <a href="mailto:support@grobotix.ai" className="font-medium text-brand underline underline-offset-2">
                support@grobotix.ai
              </a>
              .
            </p>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-line bg-bg-elev text-sm font-medium text-fg-dim transition-colors hover:bg-bg-soft hover:text-fg"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
